import React, { useEffect, useState } from "react";
import { errorLog, infoLog } from "../../utils/logger";
import SearchCard from "../../components/search/SearchCard";
import { useTranslation } from "react-i18next";
import { ImmunizationSearch, SearchStatus } from "../../models/Interfaces";
import { useNavigate } from "react-router";
import { DocketAPIError } from "../../apiClient";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { Modal } from "../../components/modals/Modal";
import "./searchList.css";
import {
  completeVerifiedRecordsAtom,
  loadableIzSearchesAtom,
  refreshingAtom,
  refreshingSearchesOrRecordsAtom,
  selectedSearchAtom,
  userAtom,
} from "../../jotai/atoms";
import { useAtom } from "jotai";
import { Squirrel } from "../../components/spinner/Squirrel";
import { hasScreeningsEnabled } from "../../globals";
import { Spinner } from "../../components/spinner/Spinner";
import { fetchRecordsUntilUpdated } from "../../utils/polling";
import { Capacitor } from "@capacitor/core";
import { Dialog } from "@capacitor/dialog";
import PullToRefresh from "react-simple-pull-to-refresh";
import { vibrateDevice } from "../../utils/capacitor";

/**
 * Calculate when we should next poll for updates.
 *
 * @param timesPolled The number of times we've already polled for data
 * @returns 0 to the maximum poll time in milliseconds, or -1 if we should stop.
 */
export function getNextPollTime(timesPolled: number): number {
  const ONE_SECOND_IN_MS = 1000;
  const minDurationMs = ONE_SECOND_IN_MS;
  const minDurationPolls = 5; // We want to attempt to do some fast polling since in MOST cases the search comes back in < 5 secs
  const maxDurationMs = 60 * 60 * ONE_SECOND_IN_MS; // One poll every hour
  // This is absurdly high on purpose. It's just to prevent idle clients from infinitely driving traffic to us.
  // It takes 12 polls (17 including the min duration polls) to hit 1 hour polling time.
  // The maximum time a message can exist in the backend SQS queue is 14 days, or 336 hours.
  // 17 + 336 = 353.
  const maxPolls = 353;

  // We poll the backend as long as we're waiting on at least 1 search or record request from the backend.
  if (timesPolled < maxPolls) {
    if (timesPolled <= minDurationPolls) {
      // We're within our 'fast polling' window so throw out the previous calculation.
      return minDurationMs;
    }

    // We want an exponential decay with full jitter.
    // Canonical article from mbrooker: https://aws.amazon.com/blogs/architecture/exponential-backoff-and-jitter/
    const expectedNextRequestTime = Math.min(
      maxDurationMs,
      minDurationMs * Math.pow(2, timesPolled - minDurationPolls)
    );
    // Add in a full jitter
    return Math.round(Math.random() * expectedNextRequestTime);
  }
  return -1;
}

export function SearchList() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [showMatchWebModal, setshowMatchWebModal] = useState(false);
  const [showNoMatchWebModal, setshowNoMatchWebModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState<string | boolean>();
  const [curSearch, setCurSearch] = useAtom(selectedSearchAtom);
  const [allSearches] = useAtom(loadableIzSearchesAtom);
  const [allVerifiedSearches] = useAtom(completeVerifiedRecordsAtom);
  const [user] = useAtom(userAtom);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [refreshing, setRefreshing] = useAtom(refreshingAtom);
  const isWeb = Capacitor.getPlatform() === "web";

  useEffect(() => {
    if (allSearches.state === "hasData") {
      setIsInitialLoad(false);
    }
  }, [allSearches.state]);

  useEffect(() => {
    // Do not want to refresh infinitely if reloading in the middle of a refresh
    setRefreshing(false);
  }, []);

  function getSearchHeader() {
    return (
      <>
        <div className="md:hidden flex mb-8">
          <p className="text-xl font-extrabold text-white mx-auto mobile-text-color">
            Immunization Search History
          </p>
        </div>
        <div className="max-md:hidden flex p-6 pl-0">
          <div className="text-black dark:text-white">
            {allSearches.state === "hasData" && (
              <h2>{`${t("immunizations.search_header_pt1")} ${allSearches.data.length} ${t(
                "immunizations.search_header_pt2"
              )}`}</h2>
            )}
          </div>
          <button
            data-testid="newSearchBtn"
            className="docket-button btn ml-auto cursor-pointer"
            onClick={async () => {
              await setCurSearch(null);
              navigate("newsearch");
            }}
          >
            {t("immunizations.search_start_new_search")}
          </button>
        </div>
      </>
    );
  }

  const showNoMatchModal = async (search: ImmunizationSearch) => {
    if (isWeb) {
      setshowNoMatchWebModal(true);
    } else {
      let noMatchTitle = "";
      let noMatchMessage: string = t("immunizations.no_match_multi_match_message");
      if (search.status === SearchStatus.basicMatchNoContacts) {
        noMatchTitle = t("immunizations.search_action_required");
        noMatchMessage = t("immunizations.search_action_required_message");
      } else if (search.status === SearchStatus.noMatch) {
        noMatchTitle = t("immunizations.no_match_alert_title");
      } else {
        noMatchTitle = t("immunizations.no_match_multi_match_alert_title");
      }

      const { value } = await Dialog.confirm({
        title: noMatchTitle,
        message: noMatchMessage,
        okButtonTitle: t("immunizations.no_match_multi_match_no_match_button"),
        cancelButtonTitle: t("immunizations.no_match_multi_match_review_button"),
      });

      navFromNoMatchModal(!value);
    }
  };

  const showMatchModal = async (search: ImmunizationSearch) => {
    if (isWeb) {
      setshowMatchWebModal(true);
    } else {
      const { value } = await Dialog.confirm({
        title: t("immunizations.records_found_alert_header"),
        message: t("immunizations.records_found_alert_message"),
        okButtonTitle: t("immunizations.records_found_view_records"),
        cancelButtonTitle: t("immunizations.records_found_edit_search"),
      });
      if (value) {
        navFromMatchModal("records", search);
      } else {
        navFromMatchModal("search", search);
      }
    }
  };

  const selectItemHandler = async (searchObj: ImmunizationSearch) => {
    await setCurSearch(searchObj);
    if (searchObj.status === SearchStatus.inQueue) {
    } else if (
      searchObj.status === SearchStatus.noRecords ||
      searchObj.status === SearchStatus.multiMatch
    ) {
      showNoMatchModal(searchObj);
    } else if (
      searchObj.status === SearchStatus.partialMatchAltContacts &&
      !searchObj.dateVerified
    ) {
      navigate("altcontacts", { state: { fromSearchList: true } });
    } else if (
      // results other than 0, like ones above, either
      // need to be result 2 or need dateVerified
      searchObj.status !== SearchStatus.noMatch &&
      searchObj.status !== SearchStatus.multiMatch &&
      searchObj.status !== SearchStatus.basicMatchNoContacts &&
      searchObj.dateVerified
    ) {
      showMatchModal(searchObj);
    } else {
      showNoMatchModal(searchObj);
    }
  };

  const getNoMatchModal = () => {
    let noMatchTitle = "";
    let noMatchMessage: string = t("immunizations.no_match_multi_match_message");
    if (curSearch?.status === SearchStatus.basicMatchNoContacts) {
      noMatchTitle = t("immunizations.search_action_required");
      noMatchMessage = t("immunizations.search_action_required_message");
    } else if (curSearch?.status === SearchStatus.noMatch) {
      noMatchTitle = t("immunizations.no_match_alert_title");
    } else {
      noMatchTitle = t("immunizations.no_match_multi_match_alert_title");
    }

    return (
      showNoMatchWebModal && (
        <Modal
          id="no-match-modal"
          open={showNoMatchWebModal}
          hasClose={true}
          title={noMatchTitle}
          onCloseModal={() => setshowNoMatchWebModal(false)}
          actionButtons={
            <>
              <button
                className="btn docket-button secondary"
                aria-label="Review search"
                onClick={() => navFromNoMatchModal(true)}
              >
                {t("immunizations.no_match_multi_match_review_button")}
              </button>
              <button
                className="btn docket-button"
                aria-label="More info"
                onClick={() => navFromNoMatchModal(false)}
              >
                {t("immunizations.no_match_multi_match_no_match_button")}
              </button>
            </>
          }
        >
          <p>{noMatchMessage}</p>
        </Modal>
      )
    );
  };

  const navFromNoMatchModal = (review: boolean) => {
    if (review) {
      navigate("newsearch");
    } else {
      navigate("results", { state: { isScenarioB: false } });
    }
    setshowNoMatchWebModal(false);
  };

  const getSearchNavModal = () => {
    return (
      showMatchWebModal && (
        <Modal
          id="search-nav-modal"
          open={showMatchWebModal}
          hasClose={true}
          title={t("immunizations.records_found_alert_header")}
          onCloseModal={() => setshowMatchWebModal(false)}
          actionButtons={
            <>
              <button
                className="btn docket-button"
                aria-label="View immunizations"
                onClick={() => navFromMatchModal("records", curSearch)}
              >
                {t("immunizations.records_found_view_records")}
              </button>

              {hasScreeningsEnabled(user?.email || "") && (
                <button
                  className="btn docket-button max-md:hidden"
                  aria-label="View Test Results"
                  onClick={() => navFromMatchModal("screenings", curSearch)}
                >
                  {t("immunizations.records_found_view_screenings")}
                </button>
              )}
              <button
                className="btn docket-button secondary"
                aria-label="Edit search"
                onClick={() => navFromMatchModal("search", curSearch)}
              >
                {t("immunizations.records_found_edit_search")}
              </button>
            </>
          }
        >
          <p>{t("immunizations.records_found_alert_message")}</p>
        </Modal>
      )
    );
  };

  const navFromMatchModal = (
    nextPage: "records" | "screenings" | "search",
    search: ImmunizationSearch | null
  ) => {
    const completeRecord = allVerifiedSearches.find((s) => s.search.uid === search?.uid);
    if (completeRecord && nextPage === "records") {
      if (completeRecord.record && completeRecord.record.immunizations.length > 0) {
        navigate("/home/records#" + allVerifiedSearches.indexOf(completeRecord));
      } else {
        navigate("/home/records");
      }
    } else if (completeRecord && nextPage === "screenings") {
      if (completeRecord.screening && completeRecord.screening.results.length > 0) {
        navigate("/home/records/screening#" + allVerifiedSearches.indexOf(completeRecord));
      } else {
        navigate("/home/records/screening");
      }
    } else {
      navigate("newsearch");
    }
    setshowMatchWebModal(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await vibrateDevice();
    await fetchRecordsUntilUpdated(true);
    setRefreshing(false);
  };

  const renderSearchContent = () => {
    if (allSearches.state === "hasError") {
      setShowErrorModal(t("immunizations.search_records_error"));
      return <></>;
    }

    if (!isInitialLoad && allSearches.state === "hasData" && allSearches.data.length === 0) {
      return (
        <div className={`flex flex-col ${!isInitialLoad ? "" : "hidden"}`}>
          <p className="text-lg text-black dark:text-white font-black mx-auto my-10 mobile-text-color">
            {t("immunizations.search_records_message")}
          </p>
          <button
            className="btn docket-button-responsive md:hidden max-md:w-full mx-auto"
            onClick={() => navigate("/home/search/newsearch")}
          >
            {t("immunizations.immunizations_add_records_button")}
          </button>
        </div>
      );
    }

    if (allSearches.state !== "hasData") return <ul></ul>;

    const searchContent = (
      <ul>
        {allSearches.data.map((search, index) => (
          <li className="mb-4" key={index}>
            <SearchCard
              search={search}
              index={index}
              onSelect={() => {
                selectItemHandler(search);
              }}
            />
          </li>
        ))}
      </ul>
    );

    return (
      <>
        <div data-testid="desktop-search-list" className="max-md:hidden">
          {searchContent}
        </div>
        <div data-testid="mobile-search-list" className="md:hidden">
          <PullToRefresh
            onRefresh={handleRefresh}
            refreshingContent={<Spinner />}
            pullingContent={<></>}
            className="pb-20"
            pullDownThreshold={78}
            maxPullDownDistance={120}
          >
            {searchContent}
          </PullToRefresh>
        </div>
      </>
    );
  };

  return (
    <div className="max-md:px-6 max-md:pb-20 max-md:pt-6">
      {isWeb && (
        <>
          {getSearchNavModal()}
          {getNoMatchModal()}
        </>
      )}
      {(allSearches.state === "loading" || isInitialLoad || refreshing) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
          <Squirrel />
        </div>
      )}
      {getSearchHeader()}
      {showErrorModal && (
        <ErrorModal onCloseModal={() => setShowErrorModal(false)}>
          <p>{showErrorModal}</p>
        </ErrorModal>
      )}
      <div className="section">{renderSearchContent()}</div>
    </div>
  );
}

export default SearchList;
